import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimationErrorGlitch } from '../styles/animation-error-glitch';

const LoadingBoxContainer = styled.div`
  ${AnimationErrorGlitch}

  border: 2px solid rgba(248, 8, 80, 0.9);
  box-shadow: 0px 0px 15px 5px rgba(248, 8, 80, 0.3),
    inset 0px 0px 15px 5px rgba(248, 8, 80, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  animation: 1s expand, 1s error-glitch 2.5s, 1s error-glitch 6s;
  max-width: 500px;
  height: 10rem;
  width: 100%;

  @keyframes expand {
    from {
      transition: all 0.7s ease-in-out;
      height: 0;
      width: 0%;
    }

    70% {
      height: 0;
      width: 100%;
    }

    to {
      height: 10rem;
      width: 100%;
    }
  }

  @keyframes shrink {
    from {
      height: 10rem;
    }

    to {
      height: 4rem;
    }
  }
`;

const LoadingTitleText = styled.span`
  color: rgba(248, 8, 80, 0.9);
  text-shadow: 0px 0px 10px rgba(248, 8, 80, 1);
  font-family: 'VT323', monospace;
  font-weight: normal;
  font-size: 2rem;
  margin-bottom: 2rem;

  &::before {
    content: '< ';
  }

  &::after {
    content: ' >';
  }
`;

const LoadingBar = styled.span`
  color: rgba(248, 8, 80, 0.9);
  text-shadow: 0px 0px 10px rgba(248, 8, 80, 1);
  font-family: 'VT323', monospace;
  font-weight: normal;
  font-size: 1.5rem;

  &::before {
    content: '[';
  }

  &::after {
    content: ']';
  }
`;

const LoadingBarEmpty = styled.span`
  color: rgba(255, 255, 255, 1);
  text-shadow: 0px 0px 10px rgba(255, 255, 255, 1);
`;

type LoadingBoxProps = {
  loadingTitle?: string;
  finalTitle: string;
};

/**
 * Should accept props for props progress: number, loadingTitle: string, finalTitle: string
 *
 */
export const LoadingBox = ({
  loadingTitle = 'LOADING',
  finalTitle,
}: LoadingBoxProps) => {
  const loaderSegments = 25;
  const [loadingTitleState, setLoadingTitleState] = useState('_');
  const [loadingBar, setLoadingBar] = useState({
    progress: '',
    empty: '.'.repeat(loaderSegments),
  });
  const [displayResult, setDisplayResult] = useState(false);
  const loadingTitleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    for (let index = 0; index < loadingTitle.length; index++) {
      setTimeout(
        () => setLoadingTitleState(loadingTitle.substring(0, index + 1)),
        1000 + index * 75
      );
    }

    let finalTitleDelay = 3000;

    for (let index = 0; index < 3; index++) {
      finalTitleDelay = finalTitleDelay + 200;
      setTimeout(
        () => setLoadingTitleState(' '.repeat(finalTitle.length)),
        finalTitleDelay
      );
      finalTitleDelay = finalTitleDelay + 200;
      setTimeout(() => setLoadingTitleState(finalTitle), finalTitleDelay);
    }
  }, [loadingTitle, finalTitle]);

  useEffect(() => {
    for (let index = 0; index < loaderSegments; index++) {
      setTimeout(
        () =>
          setLoadingBar({
            progress: '#'.repeat(index + 1),
            empty: '.'.repeat(loaderSegments - (index + 1)),
          }),
        1000 + index * 50
      );
    }
  }, [finalTitle]);

  return (
    <LoadingBoxContainer>
      {displayResult ? (
        <></>
      ) : (
        <>
          <LoadingTitleText ref={loadingTitleRef}>
            {loadingTitleState}
          </LoadingTitleText>
          <LoadingBar>
            {loadingBar.progress}
            <LoadingBarEmpty>{loadingBar.empty}</LoadingBarEmpty>
          </LoadingBar>
        </>
      )}
    </LoadingBoxContainer>
  );
};
